/**
 * Future Visit Coordination Step
 *
 * Step 2 of the Coordinate Care dialog.
 * Builds care requirements locally from the care pattern (the episode has none yet —
 * adding them to the backend auto-schedules visits, which only happens on Confirm).
 * Displays CareRequirementsEditor + admission visit summary before the user confirms.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { addDays, format } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { useOrgConfig } from '~/providers/OrgConfigProvider';
import { useZonedDateFormat } from '~/providers/ZonedDateFormatProvider';
import {
  CareRequirement,
  cloneCareRequirement,
  createNewCareRequirement,
} from '@caredna-marlin/utils/client';
import CareRequirementsEditor from '~/components/patient/CareRequirementsEditor';
import { getCareRequirementDetailedSummaryLabel } from '~/components/patient/careRequirementSummary';
import { timeLabelWithZone } from '@caredna-marlin/utils/dates';
import { SelectedAdmissionOption } from './AdmissionVisitOptionsPanel';
import { VisitType } from '~/providers/OrgConfigProvider';

const DEFAULT_EPISODE_FREQUENCY: CareRequirement['rruleFreq'] = 'daily';

interface TimeWindow {
  earliestStart: string; // 'HH:mm'
  latestStart: string;   // 'HH:mm'
}

interface FutureVisitCoordinationStepProps {
  patientTimezone: string;
  serviceLineId: number;
  selectedOption: SelectedAdmissionOption;
  admissionVisitType: VisitType | null;
  admissionDate: Date;
  expectedLOS: number;
  carePatternName: string;
  carePatternDuration: number;
  carePatternTimeWindows: TimeWindow[];
  autoScheduleToday: boolean;
}

/** Convert 'HH:mm' string to a UTC Date representing that wall-clock time in the given timezone. */
function parseTimeWindow(hhmm: string, timezone: string): Date {
  const todayInTz = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  return fromZonedTime(`${todayInTz}T${hhmm}:00`, timezone);
}

export function FutureVisitCoordinationStep({
  patientTimezone,
  serviceLineId,
  selectedOption,
  admissionVisitType,
  admissionDate,
  expectedLOS,
  carePatternName,
  carePatternDuration,
  carePatternTimeWindows,
  autoScheduleToday,
}: FutureVisitCoordinationStepProps) {
  const orgConfig = useOrgConfig();
  const { timeLabel } = useZonedDateFormat();
  const initialized = useRef(false);

  // Mirror TodaysVisitScheduler: only windows whose hour is after the admission hour count as same-day visits
  const sameDayRoutineCount = useMemo(() => {
    const admissionHour = parseInt(formatInTimeZone(new Date(selectedOption.startTime), patientTimezone, 'H'), 10);
    return carePatternTimeWindows.filter((w) => {
      const [windowHour] = w.earliestStart.split(':').map(Number);
      return windowHour > admissionHour;
    }).length;
  }, [selectedOption.startTime, patientTimezone, carePatternTimeWindows]);

  // Find the routine visit type from org config
  const routineVisitTypeId = useMemo(() => {
    const vt = Object.values(orgConfig.visitTypes).find(
      (v) => v && v.name.toLowerCase().includes('routine')
    );
    return vt ? String(vt.id) : '';
  }, [orgConfig.visitTypes]);

  // Build care requirements from the care pattern (not from the backend)
  const dischargeDate = useMemo(() => addDays(admissionDate, expectedLOS), [admissionDate, expectedLOS]);

  // Label for when future visits begin (day after admission)
  const futureCareStartLabel = useMemo(() => {
    const isAdmissionToday = format(admissionDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
    return isAdmissionToday ? 'tomorrow' : format(addDays(admissionDate, 1), 'EEEE, MMM d');
  }, [admissionDate]);

  const builtRequirements = useMemo<CareRequirement[]>(() => {
    if (!routineVisitTypeId) return [];
    return carePatternTimeWindows.map((window) => ({
      ...createNewCareRequirement({
        frequency: DEFAULT_EPISODE_FREQUENCY,
        admissionDate,
        expectedDischargeDate: dischargeDate,
      }),
      visitTypeId: routineVisitTypeId,
      duration: String(carePatternDuration),
      minTime: parseTimeWindow(window.earliestStart, patientTimezone),
      maxTime: parseTimeWindow(window.latestStart, patientTimezone),
    }));
  }, [routineVisitTypeId, admissionDate, dischargeDate, carePatternDuration, carePatternTimeWindows, patientTimezone]);

  const [careRequirements, setCareRequirements] = useState<CareRequirement[]>([]);

  useEffect(() => {
    if (builtRequirements.length > 0 && !initialized.current) {
      initialized.current = true;
      setCareRequirements(builtRequirements);
    }
  }, [builtRequirements]);

  return (
    <Box>
      {/* Admission Visit Summary */}
      <Typography variant="h6" gutterBottom>
        Admission Visit
      </Typography>
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.lighter' }} elevation={0}>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label="Scheduled"
              color="primary"
              size="small"
              icon={<CheckCircleIcon />}
            />
            <Typography variant="subtitle1" fontWeight={600}>
              {admissionVisitType?.name || 'Admission Visit'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Time
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {timeLabelWithZone(selectedOption.startTime, {
                  ampm: true,
                  timezone: patientTimezone,
                })}{' '}
                today
              </Typography>
            </Box>
            {selectedOption.providerNames.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Provider(s)
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {selectedOption.providerNames.join(', ')}
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Care Pattern */}
      <Typography variant="h6" gutterBottom>
        Care Pattern: {carePatternName}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Visits will be auto-scheduled following the below care pattern for the remainder of the {expectedLOS}-day episode beginning {futureCareStartLabel}.
      </Typography>

      <CareRequirementsEditor
        careRequirements={careRequirements}
        setCareRequirements={setCareRequirements}
        serviceLineId={serviceLineId}
        timezone={patientTimezone}
        createNewRequirement={() =>
          createNewCareRequirement({
            frequency: DEFAULT_EPISODE_FREQUENCY,
            admissionDate,
            expectedDischargeDate: dischargeDate,
          })
        }
        duplicateRequirement={(source) =>
          cloneCareRequirement(source, {
            dtstart: admissionDate,
            until: dischargeDate,
            rruleFreq: DEFAULT_EPISODE_FREQUENCY,
            rruleInterval: null,
            rruleByweekday: null,
          })
        }
        summaryLabel={(careRequirement, index) =>
          getCareRequirementDetailedSummaryLabel({
            careRequirement,
            index,
            visitTypeName: orgConfig.visitTypes[Number(careRequirement.visitTypeId)]?.name,
            timeLabel,
            timezone: patientTimezone,
          })
        }
        contentPaddingX={1}
      />

      <Divider sx={{ my: 3 }} />

      {/* Stats */}
      <Typography variant="h6" gutterBottom>
        Auto-scheduling Summary
      </Typography>
      <Paper sx={{ p: 2, bgcolor: 'info.lighter' }} elevation={0}>
        <Stack
          direction="row"
          divider={<Box sx={{ width: '1px', bgcolor: 'divider', alignSelf: 'stretch' }} />}
          sx={{ justifyContent: 'space-around' }}
        >
          <Box sx={{ textAlign: 'center', px: 2 }}>
            <Typography variant="h5" color="primary.main" fontWeight={700}>
              {carePatternTimeWindows.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Visits per Day
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', px: 2 }}>
            <Typography variant="h5" color="primary.main" fontWeight={700}>
              {1 + (autoScheduleToday ? sameDayRoutineCount : 0) + (expectedLOS - 1) * carePatternTimeWindows.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Visits
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', px: 2 }}>
            <Typography variant="h5" color="primary.main" fontWeight={700}>
              {expectedLOS}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Days of Care
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
